import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/lib/api';
import { friendlyFromApiError } from '@/lib/errors';
import { useAuthStore } from '@/hooks/useAuthStore';

/**
 * SettingsMfa — Cycle 8 MFA Hardening (D41 + D42) + Cycle 9 Backup Codes (A.6)
 *
 * C7 baseline: page supported enrollment only; disable path was SUPER_ADMIN
 *   breakglass from /staff.  State detection relied on a fragile workaround:
 *   POST /mfa/setup and read a 400 "MFA already enabled" error.
 *
 * C8 changes:
 *  - D41: `/auth/me` now returns { mfaEnabled, mfaVerified } so we derive
 *    the state from the me query result directly.  No more setup-probe.
 *  - D42: new self-service disable modal (password + TOTP).  Backend revokes
 *    refresh tokens, so on success we logout and redirect to /login.
 *
 * C9 A.6 changes:
 *  - Setup/verify response now includes `backupCodes[]` → shown in the same
 *    one-shot modal so the user can save them before the banner appears.
 *  - Active state: remaining-codes indicator + "Regenerate" action (password
 *    + TOTP re-auth, drops all unused codes).
 *  - Disable modal: toggle "Use backup code instead of TOTP" swaps the 6-digit
 *    numeric input for a 9-char dashed input (`xxxx-xxxx`).  Backend accepts
 *    either totpCode OR backupCode with the password.
 *
 * UI states derived from `/auth/me`:
 *  1. mfaEnabled && mfaVerified   → "activo"    (green banner + Disable btn)
 *  2. !mfaEnabled                 → "no config" (amber banner + Setup btn)
 *  3. mfaEnabled && !mfaVerified  → "limbo"     (yellow — should be rare
 *                                   post-C7+D34; offer restart via setup)
 */

type SetupData = { qrCodeUrl: string; secret: string };

// Local UI state — separate from the persisted MFA state on the server so
// we can show transient screens (setup QR, success banner) without mutating
// the authoritative /auth/me response.
type UIStep =
  | { kind: 'idle' } // show the state derived from meQuery
  | { kind: 'setup-started'; data: SetupData }
  | { kind: 'just-verified' }; // show ephemeral success banner

type DisableMethod = 'totp' | 'backup';

export default function SettingsMfa() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { staff, logout } = useAuthStore();
  const [uiStep, setUiStep] = useState<UIStep>({ kind: 'idle' });
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);

  // D42 modal state (kept local — no store coupling).
  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableTotp, setDisableTotp] = useState('');
  const [disableBackupCode, setDisableBackupCode] = useState('');
  const [disableMethod, setDisableMethod] = useState<DisableMethod>('totp');
  const [disableError, setDisableError] = useState<string | null>(null);

  // C9 A.6 — backup codes one-shot display modal.  The `codes` array is held
  // only in React state; we never persist it to storage so a refresh wipes it
  // (the backend will refuse to re-issue — by design).
  const [backupCodesModal, setBackupCodesModal] = useState<
    { open: true; codes: string[] } | { open: false }
  >({ open: false });

  // C9 A.6 — regenerate-backup-codes modal (password + TOTP re-auth).
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenPassword, setRegenPassword] = useState('');
  const [regenTotp, setRegenTotp] = useState('');
  const [regenError, setRegenError] = useState<string | null>(null);

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => authApi.getMe(),
  });

  const mfaEnabled = meQuery.data?.data.staff.mfaEnabled ?? false;
  const mfaVerified = meQuery.data?.data.staff.mfaVerified ?? false;
  const backupCodesRemaining = meQuery.data?.data.staff.backupCodesRemaining;

  const setupMutation = useMutation({
    mutationFn: () => authApi.setupMfa(),
    onSuccess: (resp) => {
      if (resp?.success && resp.data) {
        setError(null);
        setUiStep({ kind: 'setup-started', data: resp.data });
      } else {
        setError('Respuesta inesperada del servidor');
      }
    },
    onError: (err: unknown) => {
      setError(friendlyFromApiError(err, 'No se pudo iniciar configuracion MFA'));
    },
  });

  const verifyMutation = useMutation({
    mutationFn: (totp: string) => authApi.verifyMfaSetup(totp),
    onSuccess: (resp) => {
      setError(null);
      setCode('');
      // C9 A.6 — backend now returns `backupCodes` on first successful verify.
      // Show the modal *before* flipping to the "just-verified" banner so the
      // user cannot dismiss the codes by accident.
      if (resp?.data?.backupCodes && resp.data.backupCodes.length > 0) {
        setBackupCodesModal({ open: true, codes: resp.data.backupCodes });
      }
      setUiStep({ kind: 'just-verified' });
      // Refresh /auth/me so derived state flips to enabled+verified.
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
    onError: (err: unknown) => {
      setError(friendlyFromApiError(err, 'Codigo MFA invalido'));
    },
  });

  const disableMutation = useMutation({
    mutationFn: ({
      password,
      totp,
      backupCode,
    }: {
      password: string;
      totp?: string;
      backupCode?: string;
    }) =>
      authApi.disableMfa(password, {
        totpCode: totp,
        backupCode,
      }),
    onSuccess: () => {
      // Backend revoked refresh tokens → local session is dead.  Clear the
      // store and redirect to /login.  No need to invalidate queries.
      setDisableOpen(false);
      setDisablePassword('');
      setDisableTotp('');
      setDisableBackupCode('');
      setDisableMethod('totp');
      setDisableError(null);
      logout();
      navigate('/login', { replace: true, state: { mfaDisabled: true } });
    },
    onError: (err: unknown) => {
      setDisableError(friendlyFromApiError(err, 'No se pudo desactivar MFA'));
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: ({ password, totp }: { password: string; totp: string }) =>
      authApi.regenerateBackupCodes(password, totp),
    onSuccess: (resp) => {
      setRegenOpen(false);
      setRegenPassword('');
      setRegenTotp('');
      setRegenError(null);
      if (resp?.codes && resp.codes.length > 0) {
        setBackupCodesModal({ open: true, codes: resp.codes });
      }
      // Refresh /auth/me so `backupCodesRemaining` flips back to 10.
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
    onError: (err: unknown) => {
      setRegenError(friendlyFromApiError(err, 'No se pudieron regenerar los códigos'));
    },
  });

  const handleStart = () => {
    setError(null);
    setupMutation.mutate();
  };

  const handleVerify = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;
    verifyMutation.mutate(code);
  };

  const handleCancelSetup = () => {
    setError(null);
    setCode('');
    setUiStep({ kind: 'idle' });
  };

  const openDisableModal = () => {
    setDisablePassword('');
    setDisableTotp('');
    setDisableBackupCode('');
    setDisableMethod('totp');
    setDisableError(null);
    setDisableOpen(true);
  };

  const closeDisableModal = () => {
    if (disableMutation.isPending) return; // don't allow close mid-request
    setDisableOpen(false);
  };

  const handleDisableSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (disablePassword.length < 8) {
      setDisableError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (disableMethod === 'totp') {
      if (disableTotp.length !== 6) {
        setDisableError('El código TOTP debe tener 6 dígitos.');
        return;
      }
      setDisableError(null);
      disableMutation.mutate({ password: disablePassword, totp: disableTotp });
      return;
    }
    // backup code path: xxxx-xxxx (9 chars inc dash)
    if (!/^[a-z0-9]{4}-[a-z0-9]{4}$/i.test(disableBackupCode)) {
      setDisableError(
        'El código de backup debe tener el formato xxxx-xxxx (8 caracteres + guion).'
      );
      return;
    }
    setDisableError(null);
    disableMutation.mutate({
      password: disablePassword,
      backupCode: disableBackupCode.toLowerCase(),
    });
  };

  const openRegenModal = () => {
    setRegenPassword('');
    setRegenTotp('');
    setRegenError(null);
    setRegenOpen(true);
  };

  const closeRegenModal = () => {
    if (regenerateMutation.isPending) return;
    setRegenOpen(false);
  };

  const handleRegenSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (regenPassword.length < 8) {
      setRegenError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (regenTotp.length !== 6) {
      setRegenError('El código TOTP debe tener 6 dígitos.');
      return;
    }
    setRegenError(null);
    regenerateMutation.mutate({ password: regenPassword, totp: regenTotp });
  };

  const handleCopyCodes = async () => {
    if (!backupCodesModal.open) return;
    try {
      await navigator.clipboard.writeText(backupCodesModal.codes.join('\n'));
    } catch {
      // Clipboard may be unavailable (insecure context, permissions).  Swallow
      // silently — the "Download .txt" fallback covers this case.
    }
  };

  const handleDownloadCodes = () => {
    if (!backupCodesModal.open) return;
    const now = new Date();
    const dateStr = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')}`;
    const header = [
      'SolidCycle Admin — MFA Backup Codes',
      `Generated: ${now.toISOString()}`,
      `Account: ${staff?.email ?? 'unknown'}`,
      '',
      'Each code can be used exactly once to disable MFA if you lose your',
      'authenticator device.  Keep this file in a password manager.  You will',
      'NOT be able to view these codes again.',
      '',
    ].join('\n');
    const body = backupCodesModal.codes.join('\n');
    const blob = new Blob([header + body + '\n'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `solidcycle-backup-codes-${dateStr}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const closeBackupCodesModal = () => setBackupCodesModal({ open: false });

  // ───────────────────────────────────────────────────────────────────────
  // Derived state label for the card.  Precedence:
  //   local transient (setup-started / just-verified) > server state
  // ───────────────────────────────────────────────────────────────────────
  const showSetupFlow = uiStep.kind === 'setup-started';
  const showJustVerified = uiStep.kind === 'just-verified';
  const showActive = !showSetupFlow && !showJustVerified && mfaEnabled && mfaVerified;
  const showLimbo =
    !showSetupFlow && !showJustVerified && mfaEnabled && !mfaVerified;
  const showIdle =
    !showSetupFlow && !showJustVerified && !mfaEnabled;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">MFA - Doble factor</h1>
        <p className="text-gray-600 dark:text-gray-300">
          Configura tu aplicacion autenticadora (Google Authenticator, Authy, 1Password)
        </p>
      </div>

      {/* Card */}
      <div className="card">
        {/* Identity */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Cuenta</p>
            <p className="text-base font-medium text-gray-900 dark:text-gray-100">
              {staff?.email ?? '...'}
            </p>
          </div>
          {meQuery.isFetching && (
            <div className="animate-spin h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full" />
          )}
        </div>

        {/* Global error (setup/verify only — disable modal has its own) */}
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800 text-sm">
            {error}
          </div>
        )}

        {/* ====== State: just verified (ephemeral success banner) ====== */}
        {showJustVerified && (
          <div className="p-4 rounded-lg bg-green-50 text-green-800 border border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <div>
                <p className="font-semibold">MFA activado correctamente</p>
                <p className="text-sm mt-1">
                  El proximo login pedira un codigo de tu app autenticadora. Cierra sesion y vuelve
                  a entrar para verificar.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ====== State: active (mfaEnabled && mfaVerified) ====== */}
        {showActive && (
          <div>
            <div className="p-4 rounded-lg bg-green-50 text-green-800 border border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800 mb-4">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <div>
                  <p className="font-semibold">MFA activo</p>
                  <p className="text-sm mt-1">
                    Tu cuenta está protegida con un segundo factor. Cada login exige un código
                    TOTP de 6 dígitos de tu app autenticadora.
                  </p>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={openDisableModal}
              className="px-4 py-2 rounded-lg bg-orange-600 text-white font-medium hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Desactivar MFA
            </button>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Requiere tu contraseña actual y un código TOTP (o un código de backup). Todas tus
              sesiones se cerrarán al confirmar.
            </p>

            {/* ─── C9 A.6: Backup codes sub-section ─────────────────── */}
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Códigos de backup
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                Los códigos de backup te permiten desactivar MFA si pierdes tu app autenticadora.
                Cada código se puede usar una sola vez. Guárdalos en un password manager —
                <span className="font-semibold"> no se volverán a mostrar</span>.
              </p>

              <div className="flex items-center gap-3 mb-4">
                <div
                  className={`px-3 py-2 rounded-lg text-sm font-medium ${
                    backupCodesRemaining === undefined
                      ? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      : backupCodesRemaining === 0
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                        : backupCodesRemaining <= 3
                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                          : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                  }`}
                >
                  {backupCodesRemaining === undefined
                    ? 'Códigos disponibles: desconocido'
                    : `${backupCodesRemaining} de 10 códigos disponibles`}
                </div>
              </div>

              <button
                type="button"
                onClick={openRegenModal}
                className="btn-secondary"
              >
                Regenerar códigos de backup
              </button>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Regenerar descarta todos los códigos actuales sin usar y genera 10 nuevos.
                Requiere tu contraseña + un código TOTP.
              </p>
            </div>
          </div>
        )}

        {/* ====== State: limbo (mfaEnabled && !mfaVerified) ====== */}
        {showLimbo && (
          <div>
            <div className="p-4 rounded-lg bg-yellow-50 text-yellow-800 border border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-800 mb-4">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M5 20h14a2 2 0 001.732-3L13.732 4a2 2 0 00-3.464 0L3.268 17A2 2 0 005 20z" />
                </svg>
                <div>
                  <p className="font-semibold">Configuración MFA incompleta</p>
                  <p className="text-sm mt-1">
                    Tu cuenta inició el enrollment pero no completó la verificación. Vuelve a
                    ejecutar la configuración para regenerar el código QR y activar MFA
                    completamente. Las instalaciones incompletas se resetean automáticamente
                    tras 30 minutos.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={handleStart}
              disabled={setupMutation.isPending}
              className="btn-primary"
            >
              {setupMutation.isPending ? 'Iniciando...' : 'Reanudar configuración'}
            </button>
          </div>
        )}

        {/* ====== State: idle / not configured ====== */}
        {showIdle && (
          <div>
            <div className="p-4 rounded-lg bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800 mb-4">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M5 20h14a2 2 0 001.732-3L13.732 4a2 2 0 00-3.464 0L3.268 17A2 2 0 005 20z" />
                </svg>
                <div>
                  <p className="font-semibold">MFA no configurado</p>
                  <p className="text-sm mt-1">
                    Activa MFA para proteger tu cuenta con un segundo factor. Necesitaras una app
                    autenticadora (Google Authenticator, Authy, 1Password, etc).
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={handleStart}
              disabled={setupMutation.isPending}
              className="btn-primary"
            >
              {setupMutation.isPending ? 'Iniciando...' : 'Configurar MFA'}
            </button>
          </div>
        )}

        {/* ====== State: setup started (QR + input) ====== */}
        {showSetupFlow && uiStep.kind === 'setup-started' && (
          <div>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Paso 1: Escanea el QR
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                Abre tu app autenticadora y escanea este codigo. Se guardara una entrada
                <span className="font-mono"> SolidCycle Admin</span> para tu cuenta.
              </p>
              <div className="flex flex-col sm:flex-row items-start gap-6">
                <div className="bg-white p-3 rounded-lg border border-gray-200 dark:border-gray-600">
                  <img
                    src={uiStep.data.qrCodeUrl}
                    alt="MFA QR code"
                    className="w-48 h-48"
                  />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    Si no puedes escanear, introduce la clave manualmente:
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="block text-xs bg-gray-100 dark:bg-gray-700 p-2 rounded font-mono break-all flex-1">
                      {showSecret ? uiStep.data.secret : '********************'}
                    </code>
                    <button
                      type="button"
                      onClick={() => setShowSecret((s) => !s)}
                      className="btn-secondary text-xs whitespace-nowrap"
                    >
                      {showSecret ? 'Ocultar' : 'Mostrar'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Esta clave solo se muestra ahora. Si la pierdes, un SUPER_ADMIN debera resetear
                    tu MFA.
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleVerify} className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Paso 2: Verifica con un codigo
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                Introduce el codigo de 6 digitos que genera tu app para confirmar el enrollment.
              </p>

              <div className="flex items-end gap-3">
                <div className="flex-1 max-w-xs">
                  <label htmlFor="totp" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Codigo MFA
                  </label>
                  <input
                    id="totp"
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="input text-center text-2xl tracking-widest"
                    placeholder="000000"
                    maxLength={6}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  disabled={verifyMutation.isPending || code.length !== 6}
                  className="btn-primary"
                >
                  {verifyMutation.isPending ? 'Verificando...' : 'Verificar y activar'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelSetup}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Help footer */}
      <div className="mt-6 text-sm text-gray-500 dark:text-gray-400">
        <p>
          <span className="font-medium">Que es MFA?</span> Multi-Factor Authentication anade un
          segundo factor ademas de tu contrasena. Sin MFA, un atacante con tu contrasena entraria;
          con MFA necesita ademas tu telefono.
        </p>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          D42 modal — Disable MFA (extended in C9 A.6 with backup-code path)
          Gated behind showActive so the trigger cannot appear in other states.
         ═══════════════════════════════════════════════════════════════════ */}
      {disableOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="disable-mfa-title"
        >
          <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2
                id="disable-mfa-title"
                className="text-lg font-semibold text-gray-900 dark:text-gray-100"
              >
                Desactivar MFA
              </h2>
            </div>

            <form onSubmit={handleDisableSubmit} className="px-5 py-4 space-y-4">
              <div className="p-3 rounded-lg bg-orange-50 text-orange-800 border border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-800 text-sm">
                <p className="font-semibold">Esta acción es irreversible en la sesión actual.</p>
                <p className="mt-1">
                  Al confirmar, MFA se desactivará y todas tus sesiones se cerrarán. Tendrás que
                  iniciar sesión de nuevo sin segundo factor.
                </p>
              </div>

              {/* C9 A.6 — method toggle (TOTP vs backup code) */}
              <div className="flex items-center gap-2 border border-gray-200 dark:border-gray-700 rounded-lg p-1 bg-gray-50 dark:bg-gray-900/30">
                <button
                  type="button"
                  onClick={() => {
                    setDisableMethod('totp');
                    setDisableError(null);
                  }}
                  disabled={disableMutation.isPending}
                  className={`flex-1 px-3 py-1.5 text-sm rounded-md transition-colors ${
                    disableMethod === 'totp'
                      ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-gray-100 font-medium'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                >
                  Código TOTP
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDisableMethod('backup');
                    setDisableError(null);
                  }}
                  disabled={disableMutation.isPending}
                  className={`flex-1 px-3 py-1.5 text-sm rounded-md transition-colors ${
                    disableMethod === 'backup'
                      ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-gray-100 font-medium'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                >
                  Código de backup
                </button>
              </div>

              <div>
                <label
                  htmlFor="disable-password"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Contraseña actual
                </label>
                <input
                  id="disable-password"
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  className="input w-full"
                  autoComplete="current-password"
                  minLength={8}
                  required
                  autoFocus
                  disabled={disableMutation.isPending}
                />
              </div>

              {disableMethod === 'totp' ? (
                <div>
                  <label
                    htmlFor="disable-totp"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Código TOTP (6 dígitos)
                  </label>
                  <input
                    id="disable-totp"
                    type="text"
                    value={disableTotp}
                    onChange={(e) =>
                      setDisableTotp(e.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                    className="input w-full text-center text-2xl tracking-widest"
                    placeholder="000000"
                    maxLength={6}
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    autoComplete="one-time-code"
                    required
                    disabled={disableMutation.isPending}
                  />
                </div>
              ) : (
                <div>
                  <label
                    htmlFor="disable-backup"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Código de backup (formato xxxx-xxxx)
                  </label>
                  <input
                    id="disable-backup"
                    type="text"
                    value={disableBackupCode}
                    onChange={(e) => {
                      // Normalize: lowercase, strip spaces, keep alnum + dash,
                      // clamp to 9 chars, auto-insert dash after 4 chars.
                      const raw = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                      let v = raw.replace(/-/g, '');
                      if (v.length > 4) {
                        v = v.slice(0, 4) + '-' + v.slice(4, 8);
                      }
                      setDisableBackupCode(v);
                    }}
                    className="input w-full text-center text-xl tracking-wider font-mono"
                    placeholder="xxxx-xxxx"
                    maxLength={9}
                    autoComplete="off"
                    spellCheck={false}
                    required
                    disabled={disableMutation.isPending}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Cada código solo se puede usar una vez. Se consumirá aunque la petición falle
                    tras la validación del backend.
                  </p>
                </div>
              )}

              {disableError && (
                <div className="p-3 rounded-lg bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800 text-sm">
                  {disableError}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeDisableModal}
                  disabled={disableMutation.isPending}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={
                    disableMutation.isPending ||
                    disablePassword.length < 8 ||
                    (disableMethod === 'totp' && disableTotp.length !== 6) ||
                    (disableMethod === 'backup' &&
                      !/^[a-z0-9]{4}-[a-z0-9]{4}$/i.test(disableBackupCode))
                  }
                  className="px-4 py-2 rounded-lg bg-orange-600 text-white font-medium hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {disableMutation.isPending ? 'Desactivando...' : 'Confirmar desactivación'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          C9 A.6 — Regenerate backup codes modal
         ═══════════════════════════════════════════════════════════════════ */}
      {regenOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="regen-title"
        >
          <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2
                id="regen-title"
                className="text-lg font-semibold text-gray-900 dark:text-gray-100"
              >
                Regenerar códigos de backup
              </h2>
            </div>

            <form onSubmit={handleRegenSubmit} className="px-5 py-4 space-y-4">
              <div className="p-3 rounded-lg bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800 text-sm">
                <p>
                  Esto descartará los {backupCodesRemaining ?? 10} códigos actuales sin usar y
                  generará 10 nuevos. Solo se mostrarán una vez.
                </p>
              </div>

              <div>
                <label
                  htmlFor="regen-password"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Contraseña actual
                </label>
                <input
                  id="regen-password"
                  type="password"
                  value={regenPassword}
                  onChange={(e) => setRegenPassword(e.target.value)}
                  className="input w-full"
                  autoComplete="current-password"
                  minLength={8}
                  required
                  autoFocus
                  disabled={regenerateMutation.isPending}
                />
              </div>

              <div>
                <label
                  htmlFor="regen-totp"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Código TOTP (6 dígitos)
                </label>
                <input
                  id="regen-totp"
                  type="text"
                  value={regenTotp}
                  onChange={(e) =>
                    setRegenTotp(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  className="input w-full text-center text-2xl tracking-widest"
                  placeholder="000000"
                  maxLength={6}
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  autoComplete="one-time-code"
                  required
                  disabled={regenerateMutation.isPending}
                />
              </div>

              {regenError && (
                <div className="p-3 rounded-lg bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800 text-sm">
                  {regenError}
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeRegenModal}
                  disabled={regenerateMutation.isPending}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={
                    regenerateMutation.isPending ||
                    regenPassword.length < 8 ||
                    regenTotp.length !== 6
                  }
                  className="btn-primary"
                >
                  {regenerateMutation.isPending ? 'Regenerando...' : 'Regenerar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          C9 A.6 — Backup codes display modal (one-shot)
          Triggered from verify-success AND regenerate-success.  Users MUST
          save the codes here — backend bcrypt-hashes them and will never
          return them again.  No persistence: refreshing wipes state.
         ═══════════════════════════════════════════════════════════════════ */}
      {backupCodesModal.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="backup-codes-title"
        >
          <div className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2
                id="backup-codes-title"
                className="text-lg font-semibold text-gray-900 dark:text-gray-100"
              >
                Códigos de backup MFA
              </h2>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div className="p-3 rounded-lg bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800 text-sm">
                <p className="font-semibold">
                  Guarda estos códigos en un password manager.
                </p>
                <p className="mt-1">
                  NO se volverán a mostrar. Cada código sirve UNA sola vez para desactivar MFA si
                  pierdes tu app autenticadora. Si pierdes tu dispositivo Y los códigos, no
                  podrás acceder.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700">
                {backupCodesModal.codes.map((codeValue, idx) => (
                  <div
                    key={idx}
                    className="font-mono text-base text-center py-2 px-3 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 select-all"
                  >
                    {codeValue}
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCopyCodes}
                  className="btn-secondary flex-1"
                >
                  Copiar al portapapeles
                </button>
                <button
                  type="button"
                  onClick={handleDownloadCodes}
                  className="btn-secondary flex-1"
                >
                  Descargar .txt
                </button>
              </div>
            </div>

            <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end">
              <button
                type="button"
                onClick={closeBackupCodesModal}
                className="btn-primary"
              >
                Ya los guardé
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
